import { useEffect, useState } from "react";
import { getNounProjectIcon } from "../utils/nounProject.js";

// Client-side cache for icons
const iconCache = new Map<string, { iconUrl: string | null; timestamp: number }>();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Helper function to get cached icon from client-side cache
function getCachedIconClient(term: string): string | null | undefined {
  const cached = iconCache.get(term.toLowerCase());
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.iconUrl;
  }
  return undefined; // Cache miss or expired
}

// Helper function to set cached icon in client-side cache
function setCachedIconClient(term: string, iconUrl: string | null): void {
  iconCache.set(term.toLowerCase(), {
    iconUrl,
    timestamp: Date.now()
  });
}

interface VocabularyIconProps {
  term: string;
  meaning?: string; // English translation to use for icon search
  className?: string;
  size?: "sm" | "md" | "lg";
  showFallback?: boolean;
}

export function VocabularyIcon({ 
  term, 
  meaning,
  className = "", 
  size = "md", 
  showFallback = true 
}: VocabularyIconProps) {
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    
    setIsLoading(true);
    setHasError(false);
    setImageLoaded(false);
    
    // Use English meaning for icon search, fallback to Spanish term
    const searchTerm = meaning || term;
    
    // Check client-side cache first
    const cachedIcon = getCachedIconClient(searchTerm);
    if (cachedIcon !== undefined) {
      // Cache hit - use cached result
      setIconUrl(cachedIcon);
      setIsLoading(false);
      if (cachedIcon) {
        setImageLoaded(true);
      } else {
        setHasError(true);
        setImageLoaded(true);
      }
      return;
    }
    
    // Cache miss - fetch from API
    getNounProjectIcon(searchTerm)
      .then((url) => {
        if (!isCancelled) {
          // Cache the result (success or failure)
          setCachedIconClient(searchTerm, url);
          
          if (url) {
            setIconUrl(url);
            // Preload the image for better performance
            const img = new Image();
            img.onload = () => {
              if (!isCancelled) {
                setImageLoaded(true);
              }
            };
            img.onerror = () => {
              if (!isCancelled) {
                setHasError(true);
                setImageLoaded(true);
              }
            };
            img.src = url;
          } else {
            setHasError(true);
            setImageLoaded(true);
          }
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          // Cache the error result
          setCachedIconClient(searchTerm, null);
          setHasError(true);
          setIsLoading(false);
          setImageLoaded(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [term, meaning]);

  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12", 
    lg: "w-16 h-16"
  };

  const fallbackSizeClasses = {
    sm: "text-lg",
    md: "text-2xl",
    lg: "text-3xl"
  };

  if (isLoading && !iconUrl) {
    return (
      <div className={`${sizeClasses[size]} flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg ${className}`}>
        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (iconUrl) {
    return (
      <div className={`${sizeClasses[size]} relative ${className}`}>
        {/* Loading spinner - show until image is fully loaded */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        )}
        
        <img
          src={iconUrl}
          alt={`Icon for ${term}`}
          className={`${sizeClasses[size]} object-contain rounded-lg bg-gray-50 dark:bg-gray-800 p-1 transition-opacity duration-200 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ display: imageLoaded ? 'block' : 'none' }}
        />
      </div>
    );
  }

  if (hasError && showFallback) {
    // Show first letter of the term as fallback
    const firstLetter = term.charAt(0).toUpperCase();
    return (
      <div className={`${sizeClasses[size]} flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-lg text-blue-700 dark:text-blue-300 font-bold ${fallbackSizeClasses[size]} ${className}`}>
        {firstLetter}
      </div>
    );
  }

  return null;
}
